import React, { Component } from 'react';
import SsoFaq from '../../common/faqs/single-sign-on';
import TitleAndDescription from '../../common/title-and-description.jsx';
import TextField from '../../common/inputs/text-field';
import SelectInput from '../../common/inputs/select-input';
import { ERROR, LOADING, NONE, SUCCESS } from '../../../models/request-status';
import {
  getSSOAccountType,
  loadSSOAccount,
  saveAccountData,
  validateCredentials
} from '../api/sso';
import SSOIntegration from './sso-integration';
import {
  TCallStatus,
  TField,
  TFieldsText,
  TPlatform,
  TPlatformOptions,
  TValidations
} from './types';
import TestConnection from './test-connection';
import SSOExplanationText from './sso-explanation-text';

type Props = {
  spot: any;
};

type State = {
  validations: TValidations;
  platform?: TPlatform;
  fieldsText: TFieldsText;
  message: string;
  currentAccountType: string;
  testingCredentials: TCallStatus;
  savingChanges: TCallStatus;
  savedPlatformFieldsText: TFieldsText;
};

const platformOptions: TPlatformOptions = {
  piano: {
    value: 'piano',
    text: 'Piano.io',
    fields: ['aid', 'api_token', 'private_key', 'public_key', 'env', 'algorithm', 'user_token'],
    notRequiredFields: [],
    mobile:
      'json{"type": "reverse", "secret_var": "jwt_token", "start_endpoint": "/v1/piano/register-user"}',
    defaultValues: [{ algorithm: 'HS256' }],
    dropdownValues: [{ env: ['sandbox', 'API'] }]
  },
  gigya: {
    value: 'gigya',
    text: 'Gigya',
    fields: ['api_key', 'secret', 'data_center', 'account_id', 'user_token'],
    notRequiredFields: [],
    mobile:
      'json{"type": "reverse", "secret_var": "token", "start_endpoint": "/v1/gigya/register-user"}',
    defaultValues: []
  },
  auth0: {
    value: 'auth0',
    text: 'Auth0',
    fields: ['auth0_domain', 'resp_prefix', 'user_token'],
    notRequiredFields: ['resp_prefix'],
    mobile:
      'json{"type": "reverse", "secret_var": "access_token", "start_endpoint": "/v1/auth0/register-user"}',
    defaultValues: []
  },
  janrain: {
    value: 'janrain',
    text: 'Janrain',
    fields: ['app_name', 'user_token'],
    notRequiredFields: [],
    mobile:
      'json{"type": "reverse", "secret_var": "janrain_oauth_token", "start_endpoint": "/v1/janrain/register-user"}',
    defaultValues: []
  },
  other: {
    value: 'other',
    text: 'Other',
    fields: [],
    notRequiredFields: [],
    mobile: '',
    defaultValues: []
  }
};

class SingleSignOn extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      validations: {},
      fieldsText: {},
      savedPlatformFieldsText: {},
      currentAccountType: '',
      message: '',
      testingCredentials: NONE,
      savingChanges: NONE
    };
  }

  componentDidMount = async () => {
    const platform = await this.getPlatformData();
    this.handlePlatformChange(platform);
  };

  getPlatformData = async () => {
    try {
      let platform;
      const { spot } = this.props;
      const spotId = spot.id;

      const accountType = await this.getAccountType(spotId);

      if (accountType) {
        const { value } = accountType;
        const data = await loadSSOAccount(spotId, value);
        const updatedFields = {
          ...data,
          spot_id: spotId,
          user_token: ''
        };
        this.setState({
          savedPlatformFieldsText: updatedFields,
          currentAccountType: value
        });
        platform = platformOptions[value];
      } else {
        platform = platformOptions['other'];
      }
      return platform;
    } catch (e) {
      return platformOptions['other'];
    }
  };

  getAccountType = async spotId => {
    const {
      tenant: {
        sso: { account_type: accountType }
      }
    } = await getSSOAccountType(spotId);

    return accountType;
  };

  handlePlatformChange = (platform: TPlatform) => {
    const { currentAccountType, savedPlatformFieldsText } = this.state;
    if (currentAccountType === platform.value) {
      this.setState({ fieldsText: savedPlatformFieldsText });
    } else {
      this.initFieldsText(platform);
    }
    this.setState({
      platform,
      message: '',
      validations: {},
      testingCredentials: NONE,
      savingChanges: NONE
    });
  };

  initFieldsText = (platform: TPlatform) => {
    const { spot } = this.props;
    const spotId = spot.id;
    let fieldsText = { spot_id: spotId };
    platform.fields.forEach(field => {
      const dropdownItem =
        platform.dropdownValues && platform.dropdownValues.find(item => item[field]);
      const dropdownValue = dropdownItem && dropdownItem[field][0];
      fieldsText = {
        ...fieldsText,
        [field]: dropdownValue || platform.defaultValues[field] || ''
      };
    });
    this.setState({ fieldsText });
  };

  validateFields = () => {
    const {
      fieldsText,
      platform: { notRequiredFields }
    } = this.state;
    let gotError = false;
    const validations: TValidations = {};
    Object.keys(fieldsText).forEach((field: TField) => {
      const isFieldNotRequired = notRequiredFields.includes(field);
      if (!isFieldNotRequired && !fieldsText[field]) {
        gotError = true;
        validations[field] = {
          message: 'Field is empty',
          status: 'error'
        };
      }
    });
    this.setState({ validations });
    return gotError;
  };

  testConnection = async () => {
    const { platform, fieldsText } = this.state;
    const gotError = this.validateFields();
    if (gotError) {
      this.setState({ testingCredentials: ERROR, message: 'Fields cannot be empty' });
      return;
    }
    this.setState({ testingCredentials: LOADING, savingChanges: NONE, message: '' });

    try {
      const { success, message } = await validateCredentials(platform.value, fieldsText);
      if (success) {
        this.setState({
          testingCredentials: SUCCESS,
          message: 'Credentials verified successfully'
        });
      } else {
        this.setState({
          testingCredentials: ERROR,
          message
        });
      }
    } catch (e) {
      this.setState({ testingCredentials: ERROR, message: 'Testing credentials failed' });
    }
  };

  saveChanges = async () => {
    const { currentAccountType, fieldsText, platform } = this.state;
    const { spot } = this.props;
    this.setState({ savingChanges: LOADING, message: '' });
    try {
      await saveAccountData(platform, spot, fieldsText, currentAccountType);
      this.setState({ savingChanges: SUCCESS, message: 'Changes successfully saved!' });
    } catch (e) {
      this.setState({ savingChanges: ERROR, message: 'Saving changes failed' });
    }
  };

  onTextFieldChange = (value: string, name: string) => {
    const { fieldsText, testingCredentials } = this.state;
    this.setState({ fieldsText: { ...fieldsText, [name]: value } });
    if (testingCredentials) {
      this.setState({ testingCredentials: NONE, message: '' });
    }
  };

  handleOnSelectChange = (field, { value }) => {
    const { fieldsText } = this.state;
    this.setState({ fieldsText: { ...fieldsText, [field]: value } });
  };

  render() {
    const {
      platform,
      validations,
      message,
      testingCredentials,
      savingChanges,
      fieldsText
    } = this.state;
    const { spot } = this.props;
    const spotId = spot.id;
    const gotError = savingChanges === ERROR || testingCredentials === ERROR;
    const options = [
      platformOptions.piano,
      platformOptions.gigya,
      platformOptions.auth0,
      platformOptions.janrain,
      platformOptions.other
    ];
    const isRegularSSO = platform && platform.value === 'other';

    if (!platform) {
      return (
        <div className="spinner-container">
          <span className="spinner loading-messages"></span>
        </div>
      );
    }
    return (
      <React.Fragment>
        <div className="preferences-sso sidebar-layout">
          <div className="main-content">
            <div className="fields">
              <TitleAndDescription title="Single Sign On" />
              <SelectInput
                title="User Management Platform"
                onChange={this.handlePlatformChange}
                options={options}
                value={platform}
              />
              {!isRegularSSO && <SSOExplanationText isRegularSSO={isRegularSSO} />}
              {(platform.fields || []).map(field => {
                const isFieldNotRequired = platform.notRequiredFields.includes(field);
                const dropdownValues =
                  platform.dropdownValues && platform.dropdownValues.find(item => item[field]);
                if (dropdownValues) {
                  const dropdownOptions = dropdownValues[field].map(item => {
                    return { text: item, value: item };
                  });
                  const selectedOption = dropdownOptions.find(
                    item => item['value'] === fieldsText[field]
                  );
                  return (
                    <SelectInput
                      key={field}
                      className="select-input-sso"
                      title={field}
                      options={dropdownOptions}
                      value={selectedOption}
                      onChange={value => this.handleOnSelectChange(field, value)}
                    />
                  );
                } else {
                  return (
                    <TextField
                      type="text"
                      key={field}
                      name={field}
                      className="outside"
                      placeholder={`Paste your ${field} here...`}
                      onChange={this.onTextFieldChange.bind(this)}
                      title={`${field}${isFieldNotRequired ? ' (optional)' : ''}`}
                      validation={validations[field]}
                      showValidationTooltip
                      value={fieldsText[field]}
                    />
                  );
                }
              })}
            </div>
            {isRegularSSO ? (
              <React.Fragment>
                <SSOExplanationText isRegularSSO={isRegularSSO} />
                <SSOIntegration spotId={spotId} />
              </React.Fragment>
            ) : (
              <TestConnection
                testConnection={this.testConnection}
                saveChanges={this.saveChanges}
                testingCredentials={testingCredentials}
                savingChanges={savingChanges}
              />
            )}
          </div>
          <SsoFaq platform={platform} />
        </div>
        {message && (
          <div className="message-area-sso">
            <div className={`message-text-sso ${gotError ? 'error-text' : 'success-text'}`}>
              <span>{message}</span>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default SingleSignOn;
